import clsx, { type ClassValue } from "clsx";
import { extendTailwindMerge } from "tailwind-merge";

/**
 * The branded type scale — every `--text-<name>` registered in
 * `app/styles/themes/theme-inline.css`. Each one emits a `text-<name>` utility
 * that sets font-size + line-height + font-weight + letter-spacing together.
 *
 * tailwind-merge only knows Tailwind's OWN size names (`xs`…`9xl`); any other
 * `text-<word>` falls through to its `text-color` group. Left unregistered,
 * `cn("text-body-sm", "text-destructive")` files both under `text-color` and
 * keeps only the last — so class ORDER inside a single `cn()` silently decides
 * whether an element gets its size or its colour, and one of the two is
 * dropped. Registering the scale as `font-size` makes size and colour
 * independent again.
 *
 * Keep in sync with the `--text-*` block in theme-inline.css. Note the near
 * miss: `text-body` / `text-body-strong` are ink COLOURS from the same file and
 * must NOT be listed here — only the `body-md` / `body-sm` steps are sizes.
 */
const BRANDED_FONT_SIZES = [
	"display-xl",
	"display-lg",
	"display-md",
	"display-sm",
	"title-lg",
	"title-md",
	"title-sm",
	"body-md",
	"body-sm",
	"caption",
	"overline",
	"code",
	"button",
	"nav",
];

const twMerge = extendTailwindMerge({
	extend: { classGroups: { "font-size": [{ text: BRANDED_FONT_SIZES }] } },
});

export function cn(...inputs: ClassValue[]): string {
	return twMerge(clsx(inputs));
}

// Truncation constants - ellipsis is 1 character (\u2026)
const TRUNCATE_ELLIPSIS_LENGTH = 1;

function _truncate(text: string, maxLength = 15): string {
	if (typeof text !== "string") {
		return "";
	}
	if (text.length <= maxLength) {
		return text;
	}
	return `${text.slice(0, maxLength - TRUNCATE_ELLIPSIS_LENGTH)}\u2026`;
}

// Regex patterns for time parsing - defined at top level for performance
const TIME_24HOUR_FORMAT_REGEX = /^\d{2}:\d{2}$/;
const AMPM_SUFFIX_REGEX = /(AM|PM)$/;
const AMPM_REPLACE_REGEX = /\s*(AM|PM)$/;
const PM_SUFFIX_REGEX = /PM$/;

// Time conversion constants
const HOURS_IN_HALF_DAY = 12;
const MIDNIGHT_HOUR = 0;
const PADDING_WIDTH = 2;
const PADDING_CHAR = "0";
const MAX_HOUR = 23;
const MAX_MINUTE = 59;
const DEFAULT_TIME = "00:00";

// Normalize various time formats to 24h HH:MM
export function normalizeTimeTo24Hour(time: string): string {
	try {
		if (!time) {
			return "00:00";
		}
		const trimmed = time
			.trim()
			.toUpperCase()
			.replace("ص", "AM")
			.replace("م", "PM");
		// If already HH:MM
		if (TIME_24HOUR_FORMAT_REGEX.test(trimmed)) {
			return trimmed;
		}
		// If H:MM AM/PM
		const ampm = AMPM_SUFFIX_REGEX.test(trimmed);
		const [hPart, mPartRaw] = trimmed
			.replace(AMPM_REPLACE_REGEX, "")
			.split(":");
		const minute = mPartRaw ? Number.parseInt(mPartRaw, 10) : 0;
		let hour = Number.parseInt(hPart || "0", 10);
		if (ampm) {
			const isPM = PM_SUFFIX_REGEX.test(trimmed);
			if (isPM && hour < HOURS_IN_HALF_DAY) {
				hour += HOURS_IN_HALF_DAY;
			}
			if (!isPM && hour === HOURS_IN_HALF_DAY) {
				hour = MIDNIGHT_HOUR;
			}
		}
		const hh = String(Math.max(0, Math.min(MAX_HOUR, hour))).padStart(
			PADDING_WIDTH,
			PADDING_CHAR,
		);
		const mm = String(Math.max(0, Math.min(MAX_MINUTE, minute))).padStart(
			PADDING_WIDTH,
			PADDING_CHAR,
		);
		return `${hh}:${mm}`;
	} catch {
		return DEFAULT_TIME;
	}
}

/**
 * Generates a UUID v4 string
 * Following ai-chatbot pattern for stable, collision-free IDs
 */
export function generateUUID(): string {
	// biome-ignore-start lint/suspicious/noBitwiseOperators: standard UUID v4 bit-masking (truncate to int, force variant bits), not boolean logic
	return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
		const r = (Math.random() * 16) | 0;
		const v = c === "x" ? r : (r & 0x3) | 0x8;
		return v.toString(16);
	});
	// biome-ignore-end lint/suspicious/noBitwiseOperators: end UUID v4 bit-masking
}
